// AdviserDetailsPage.jsx
import { useEffect, useMemo, useState, useCallback, act } from "react";
import { useLocation, useParams } from "react-router-dom";
import Sidebar from "../../DesignSystem/Sidebar";
import { Helmet } from "react-helmet-async";
import { subscribeAdviserFullDetailsByEmailOrId } from "../../../context/Adviser/Utils/AdviserLiveDetails";
import "../../../css/Adviser/AdviserDetailsPage.css";
import {
  safe,
  asList,
  formatAddress,
  formatUKPhone,
  initialsFrom,
  cssColor,
  fmtMaybeDate,
  fmtMaybeMonthYear,
  statusColor,
  QUAL_GROUPS,
  pickTruthy,
} from "../Utils/AdviserFormatters";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, updateDoc } from "firebase/firestore";
import { db, storage } from "../../../backend/firebaseConfig";
import { useUserPermissions } from "../../SignIn/UserPermissionsContext";
import { useUserEmail } from "../../SignIn/UserEmailContext";
import { useCanUploadProfile } from "../../../context/Adviser/Utils/StaffUtils/EditPermissions";
import { useNavigate } from "react-router-dom";
import Tabs from "../../DesignSystem/Tabs/Tabs";
import Breadcrumb from "../../DesignSystem/Breadcrumb/Breadcrumb";
import { a } from "react-spring";
import AdviserDetails from "../Tabs/AdviserDetails/AdviserDetails";
import ClientAgreement from "../Tabs/ClientAgreement/ClientAgreement";
import { Chip, PostCard, Row } from "../Tabs/HelperFunctions/HelperFunctions";
import ProfessionalQualifications from "../Tabs/ProfessionalQualifications/ProfessionalQualifications";
import PrimaryButton from "../../DesignSystem/Buttons/PrimaryButton";
import AlertBanner from "../../DesignSystem/AlertBanner/AlertBanner";
import FinancialPromotionsTab from "../Tabs/FinancialPromotions/FinancialPromotionsTab";
import AdviserAMLTab from "../Tabs/AML/AdviserAMLTab";
import { ReactComponent as WebsiteIcon } from "../../../assets/icons/financialPromotions/website-1.svg";
import { ReactComponent as LinkedInIcon } from "../../../assets/icons/financialPromotions/linkedIn.svg";
import { ReactComponent as FacebookIcon } from "../../../assets/icons/financialPromotions/facebook.svg";
import { ReactComponent as InstagramIcon } from "../../../assets/icons/financialPromotions/instagram.svg";
import { ReactComponent as TikTokIcon } from "../../../assets/icons/financialPromotions/tiktok.svg";
import LicenseCertificatesTab from "../Tabs/LicenseCertificates/Pages/LicenseCertificatesTab";
import ParaplanningAdviserTab from "../Tabs/Paraplanning/ParaplanningAdviserTab";
import ProCedingAdviserTab from "../Tabs/ProCeding/ProCedingAdviserTab";
import RatesTab from "../Tabs/Rates/RatesTab";
import { consumeRatesSaveSuccess } from "../Tabs/Rates/ratesNavigation";
import IODetailsTab from "../Tabs/IODetails/IODetailsTab";
import AdviserChargesTab from "../Tabs/Charges/AdviserChargesTab";
import AdviserCatchUpPanel from "../Tabs/AdviserCatchUp/AdviserCatchUpPanel";
import { isConfiguredAdviserCatchUpUser } from "../Tabs/AdviserCatchUp/adviserCatchUpConfig";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { downloadAdviserLogo } from "../Utils/downloadAdviserLogo";
import { getVisibleAdviserTabs } from "../Utils/adviserTabAccess";
import { canAccessDepartmentRoute } from "../../Departments/utils/departmentConfig";
import { APP_ROUTES } from "../../../constants/appRoutes";

/* ---------------- page ---------------- */

const getExternalUrl = (value) => {
  const trimmed = String(value || "").trim();

  if (!trimmed) return "";

  if (/^https?:\/\//i.test(trimmed)) {
    return trimmed;
  }

  return `https://${trimmed}`;
};

const normaliseHexColour = (value) => {
  const raw = String(value || "").trim().replace(/^#/, "");
  if (/^[0-9a-f]{3}$/i.test(raw)) {
    return `#${raw
      .split("")
      .map((character) => character + character)
      .join("")}`.toUpperCase();
  }
  return /^[0-9a-f]{6}$/i.test(raw) ? `#${raw.toUpperCase()}` : "";
};

// Match the export palette: 70% and 40% primary colour blended with white.
const lighterHexColour = (hex, strength) =>
  `#${[1, 3, 5].map((offset) =>
    Math.round(parseInt(hex.slice(offset, offset + 2), 16) * strength + 255 * (1 - strength))
      .toString(16).padStart(2, "0"),
  ).join("").toUpperCase()}`;

const SocialIconLink = ({ label, value, Icon }) => {
  const url = getExternalUrl(value);

  if (!url) return null;

  return (
    <button
      type="button"
      className="adviserSocialIconButton"
      onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
      title={`Open ${label}`}
      aria-label={`Open ${label}`}
    >
      <Icon />
    </button>
  );
};

export default function AdviserDetailsPage() {
  const location = useLocation();
  const { state } = location;
  const params = useParams();
  // const { userPermissions } = useUserPermissions();

  const rawKey =
    params.adviserKey || params.id || params.email || params.adviserId || "";
  const adviserKey = rawKey ? decodeURIComponent(rawKey) : "";
  const requestedTab = new URLSearchParams(location.search).get("tab");
  const [activeTab, setActiveTab] = useState(
    requestedTab || state?.activeTab || "adviserDetails",
  );

  const [details, setDetails] = useState(null);
  const proQuals = details?.professionalQualifications;
  const [uploadingProfile, setUploadingProfile] = useState(false);
  const [profileUploadError, setProfileUploadError] = useState("");
  const canUploadProfile = useCanUploadProfile();
  const [editingWebsite, setEditingWebsite] = useState(false);
  const [websiteValue, setWebsiteValue] = useState("");
  const [savingWebsite, setSavingWebsite] = useState(false);
  const [websiteError, setWebsiteError] = useState("");
  const [copiedColour, setCopiedColour] = useState("");
  const [downloadingLogo, setDownloadingLogo] = useState("");
  const [logoDownloadError, setLogoDownloadError] = useState("");
  const [successPopup, setSuccessPopup] = useState(
    state?.successMessage ||
      consumeRatesSaveSuccess([adviserKey, state?.adviserEmail]),
  );
  const { userPermissions } = useUserPermissions();
  const { userEmail } = useUserEmail();
  const navigate = useNavigate();

  useEffect(() => {
    if (!state?.successMessage) return;

    setSuccessPopup(state.successMessage);
    const nextState = { ...state };
    delete nextState.successMessage;

    navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: nextState,
    });
  }, [location.pathname, location.search, navigate, state]);

  useEffect(() => {
    if (!successPopup) return undefined;

    const timeoutId = window.setTimeout(() => setSuccessPopup(""), 5000);
    return () => window.clearTimeout(timeoutId);
  }, [successPopup]);

  // const canUploadProfile = permissions?.includes("Adviser Support");

  // console.log("User permissions:", userPermissions);

  const handleProfileImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setUploadingProfile(true);
      setProfileUploadError("");

      // optional validation
      if (!file.type.startsWith("image/")) {
        throw new Error("Please upload an image file.");
      }

      // adjust this depending on how your adviser doc id is stored
      const adviserEmail =
        adviserKey || details?.adviserDoc?.email || details?.email || "";

      if (!adviserEmail) {
        throw new Error("Adviser email not found.");
      }

      const fileExt = file.name.split(".").pop();
      const filePath = `advisers/${adviserEmail}/profile/profile.${fileExt}`;
      const storageRef = ref(storage, filePath);

      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);

      // adjust collection name/path if needed
      const adviserRef = doc(
        db,
        "Authorized Members",
        "Advisers",
        "Data",
        adviserEmail,
      );
      // /Authorized Members/Advisers/Data/

      await updateDoc(adviserRef, {
        profileUrl: downloadURL,
      });

      // optional local UI update so it changes immediately
      setDetails((prev) => ({
        ...prev,
        adviserDoc: {
          ...(prev?.adviserDoc || {}),
          profileUrl: downloadURL,
        },
      }));
    } catch (err) {
      console.error("Profile upload failed:", err);
      setProfileUploadError(err.message || "Failed to upload profile image.");
    } finally {
      setUploadingProfile(false);
    }
  };

  const handleSaveWebsite = useCallback(async () => {
    try {
      setSavingWebsite(true);
      setWebsiteError("");

      const adviserEmail =
        adviserKey || details?.adviserDoc?.email || details?.email || "";

      if (!adviserEmail) {
        throw new Error("Adviser email not found.");
      }

      let website = (websiteValue || "").trim();

      if (website && !/^https?:\/\//i.test(website)) {
        website = `https://${website}`;
      }

      const adviserRef = doc(
        db,
        "Authorized Members",
        "Advisers",
        "Data",
        adviserEmail,
      );

      await updateDoc(adviserRef, {
        website,
      });

      setDetails((prev) => ({
        ...prev,
        adviserDoc: {
          ...(prev?.adviserDoc || {}),
          website,
        },
      }));

      setWebsiteValue(website);
      setEditingWebsite(false);
    } catch (err) {
      console.error("Website save failed:", err);
      setWebsiteError(err.message || "Failed to save website.");
    } finally {
      setSavingWebsite(false);
    }
  }, [adviserKey, details, websiteValue]);

  useEffect(() => {
    if (!adviserKey) return;
    return subscribeAdviserFullDetailsByEmailOrId(adviserKey, setDetails);
  }, [adviserKey]);

  useEffect(() => {
    setWebsiteValue(details?.adviserDoc?.website || state?.website || "");
  }, [details, state]);

  const loading = !details || details.loading;

  // const canViewLicenceTabs = useMemo(() => {
  //   const permissions = Array.isArray(userPermissions) ? userPermissions : [];

  //   return permissions.some((permission) => {
  //     const value = String(permission || "")
  //       .trim()
  //       .toLowerCase();

  //     return (
  //       // value.includes("admin") ||
  //       value.includes("licence certificates")
  //       // ||
  //       // value.includes("finance") ||
  //       // value.includes("compliance")
  //     );
  //   });
  // }, [userPermissions]);

  /**
   * Build header using:
   * 1) LIVE details (personalDetails + top-level)
   * 2) fallback to router state
   */
  const header = useMemo(() => {
    const p = details?.personalDetails || {};
    const base = details?.adviserDoc || {}; // base doc (live)
    const a = state || {}; // fallback if still present

    const nameFromLive = [p?.forename, p?.surname]
      .filter(Boolean)
      .join(" ")
      .trim();

    return {
      name:
        nameFromLive ||
        a.name ||
        [a.firstName, a.lastName].filter(Boolean).join(" ") ||
        "Adviser",

      email: base?.email || details?.email || a.email || "-",

      website: base?.website || a.website || "",
      linkedin:
        base?.linkedin || base?.linkedIn || a.linkedin || a.linkedIn || "",
      facebook: base?.facebook || a.facebook || "",
      instagram: base?.instagram || a.instagram || "",
      tiktok: base?.tiktok || base?.tikTok || a.tiktok || a.tikTok || "",

      status: asList(base?.status ?? a.status),
      isAR: !!(base?.isAR ?? a.isAR),
      isIndroducerAR: !!(base?.isIndroducerAR ?? a.isIndroducerAR),
      primaryColor:
        base?.PrimaryColor ?? base?.primaryColor ?? a.primaryColor ?? "",
      secondaryColor: base?.SecondaryColor ?? base?.secondaryColor ?? "",
      tertiaryColor: base?.TertiaryColor ?? base?.tertiaryColor ?? "",

      tradingStyle:
        base?.tradingStyle ||
        base?.tradingName ||
        asList(base?.tradingStyles)[0] ||
        a.tradingStyle ||
        "",

      tradingName: base?.tradingName || a.tradingName || "",
      tradingStyles: asList(base?.tradingStyles ?? a.tradingStyles),
      fcaNumber: base?.fcaNumber || a.fcaNumber || "",

      businessAddress:
        p?.businessAddress?.fullAddress || a.businessAddress || "",

      logoUrl: details?.logoUrl || a.logoUrl || "",
      profileUrl: base?.profileUrl || details?.profileUrl || a.profileUrl || "",
    };
  }, [details, state]);

  const primaryColourValue = normaliseHexColour(header.primaryColor);

  const handleDownloadLogo = async (format) => {
    setDownloadingLogo(format);
    setLogoDownloadError("");
    try {
      await downloadAdviserLogo(header.logoUrl, header.tradingStyle || header.name, format);
    } catch (error) {
      setLogoDownloadError("Unable to download the logo. Please try again.");
    } finally {
      setDownloadingLogo("");
    }
  };

  const adviserColours = primaryColourValue
    ? [
        { key: "primary", label: "Primary colour", value: primaryColourValue },
        {
          key: "secondary",
          label: "Secondary colour",
          value: normaliseHexColour(header.secondaryColor) || lighterHexColour(primaryColourValue, 0.7),
        },
        {
          key: "tertiary",
          label: "Tertiary colour",
          value: normaliseHexColour(header.tertiaryColor) || lighterHexColour(primaryColourValue, 0.4),
        },
      ]
    : [];
  const hasSocialLinks = [
    header.website,
    header.linkedin,
    header.facebook,
    header.instagram,
    header.tiktok,
  ].some(Boolean);

  const copyColour = async (key, value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedColour(key);
      window.setTimeout(
        () => setCopiedColour((current) => (current === key ? "" : current)),
        2000,
      );
    } catch {
      setCopiedColour("");
    }
  };

  const statusBg = useMemo(
    () => statusColor(asList(header.status)),
    [header.status],
  );

  const avatarText = useMemo(() => initialsFrom(header.name), [header.name]);

  const tabs = useMemo(() => {
    const canViewAdviserCatchUp =
      userPermissions.includes("Administrator") ||
      isConfiguredAdviserCatchUpUser(userEmail);

    return getVisibleAdviserTabs(userPermissions, {
        isIntroducer: header.isIndroducerAR,
        userEmail,
      }).filter((tab) => tab.id !== "adviserCatchUp" || canViewAdviserCatchUp);
  }, [header.isIndroducerAR, userEmail, userPermissions]);

  useEffect(() => {
    if (!tabs.length) return;
    if (
      requestedTab &&
      requestedTab !== activeTab &&
      tabs.some((tab) => tab.id === requestedTab)
    ) {
      setActiveTab(requestedTab);
      return;
    }
    if (!tabs.some((tab) => tab.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [activeTab, requestedTab, tabs]);

  return (
    <div className="main-content">
      {successPopup ? (
        <div
          style={{
            position: "fixed",
            top: "24px",
            right: "24px",
            zIndex: 10000,
            width: "min(420px, calc(100vw - 48px))",
          }}
        >
          <AlertBanner
            type="success"
            message={successPopup}
            onClose={() => setSuccessPopup("")}
          />
        </div>
      ) : null}

      <Helmet>
        <title>{header.name} • Adviser</title>
      </Helmet>

      <Sidebar />

      <div className="Main-Container">
        <div className="smProfilePage">
          <div className="smDetailsBreadcrumb">
            <Breadcrumb
              items={[
                {
                  label: "All Staff",
                  to: "/Department/AllStaff",
                },
                { label: "Advisers", to: "/Advisers" },
                { label: header.name, ...(activeTab === "clientAgreement" ? { to: `/advisers/${encodeURIComponent(adviserKey || "")}` } : {}) },
                ...(activeTab === "clientAgreement" ? [{ label: "Client Agreement" }] : []),
              ]}
            />
          </div>

          {/* Cover */}
          <div
            className="smCover profileBanner"
            style={{ background: cssColor(header.primaryColor) }}
          >
            <div className="smCoverInner">
              <h3 className="smCoverTitle">
                {header.tradingStyle || header.name}
              </h3>
              {header.fcaNumber ? (
                <div className="smCoverSub Content-Small">
                  FRN {header.fcaNumber}
                </div>
              ) : null}
            </div>
            {header.logoUrl && (
              <div className="profileHeaderRight">
                <div className="profileLogoRight">
                  <img
                    className="smLogoImg-directory"
                    src={header.logoUrl}
                    alt="Logo"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "";
                      e.currentTarget.style.display = "none";
                    }}
                  />
                </div>
                {/* <img
                src={adviser.logoUrl}
                alt="Logo"
                loading="lazy"
                onError={(e) => {
                  e.currentTarget.onerror = null; // stop loops
                  e.currentTarget.src = ""; // remove request
                  e.currentTarget.style.display = "none";
                }}
              /> */}
              </div>
            )}
          </div>

          {/* Profile header */}
          <div className="smHeader">
            <div className="smHeaderLeft">
              <div className="smIdentity">
                <div className="smNameRow">
                  <h2 className="smName Content-SemiBold">{header.name}</h2>
                  {header.isAR ? <span className="smBadge">AR</span> : null}
                  {header.isIndroducerAR ? (
                    <span className="smBadge">IAR</span>
                  ) : null}
                </div>

                <div className="smBio Content-Small">
                  {header.tradingStyle ? header.tradingStyle : "-"}
                  {header.fcaNumber ? (
                    <>
                      <span className="dot"> •</span> FRN {header.fcaNumber}
                    </>
                  ) : null}
                </div>
                <div className="smBio Content-Small">
                  <a href={`mailto:${header.email}`}>{header.email}</a>
                </div>
                <div className="smMetaGroups">
                  {/* STATUS */}
                  <div className="smMetaGroup">
                    <div className="smMetaLabel Content-Small muted">
                      Status
                    </div>
                    {asList(header.status).length > 0 ? (
                      <div className="smChips">
                        {asList(header.status)
                          .slice(0, 6)
                          .map((s) => (
                            <Chip
                              key={`st-${s}`}
                              style={{ background: statusBg, color: "#fff" }}
                            >
                              {s}
                            </Chip>
                          ))}
                      </div>
                    ) : (
                      <div className="Content-Small muted">-</div>
                    )}
                  </div>

                  {/* TRADING STYLES */}
                  <div className="smMetaGroup">
                    <div className="smMetaLabel Content-Small muted">
                      Trading styles
                    </div>

                    {asList(header.tradingStyles).length > 0 ? (
                      <div className="smChips">
                        {asList(header.tradingStyles)
                          .slice(0, 6)
                          .map((s) => (
                            <Chip key={`ts-${s}`} className="smChipTrading">
                              {s}
                            </Chip>
                          ))}
                      </div>
                    ) : (
                      <div className="Content-Small muted">-</div>
                    )}
                  </div>
                {/* Actions */}
                <div className="adviserBrandingRow">
                <div className="smMetaGroup">
                  <div className="smMetaLabel Content-Small muted">Social Media and Branding</div>
                  <div className="adviserSocialIconsRow">
                    <SocialIconLink
                      label="Website"
                      value={header.website}
                      Icon={WebsiteIcon}
                    />
                    <SocialIconLink
                      label="LinkedIn"
                      value={header.linkedin}
                      Icon={LinkedInIcon}
                    />
                    <SocialIconLink
                      label="Facebook"
                      value={header.facebook}
                      Icon={FacebookIcon}
                    />
                    <SocialIconLink
                      label="Instagram"
                      value={header.instagram}
                      Icon={InstagramIcon}
                    />
                    <SocialIconLink
                      label="TikTok"
                      value={header.tiktok}
                      Icon={TikTokIcon}
                    />
                    <div className="adviserColourList Content-Small">
                    {hasSocialLinks ? (
                      <span
                        className="adviserSocialColourDot"
                        aria-hidden="true"
                      >
                        &bull;
                      </span>
                    ) : null}
                      {adviserColours.length ? (
                        adviserColours.map((colour) => (
                          <div
                            className="adviserPrimaryColour"
                            key={colour.key}
                          >
                            <span className="adviserColourLabel">
                              {colour.label}
                            </span>
                            <span
                              className="adviserPrimaryColourSwatch"
                              style={{ backgroundColor: colour.value }}
                              aria-hidden="true"
                            />
                            <b>{colour.value}</b>
                            <button
                              type="button"
                              className={`adviserPrimaryColourCopy ${
                                copiedColour === colour.key ? "is-copied" : ""
                              }`}
                              onClick={() =>
                                copyColour(colour.key, colour.value)
                              }
                              aria-label={`Copy ${colour.label.toLowerCase()} ${colour.value}`}
                              title={
                                copiedColour === colour.key
                                  ? "Copied!"
                                  : `Copy ${colour.label.toLowerCase()}`
                              }
                            >
                              <ContentCopyIcon fontSize="small" />
                            </button>
                            {copiedColour === colour.key ? (
                              <span
                                className="adviserColourCopied"
                                role="status"
                                aria-live="polite"
                              >
                                Copied
                              </span>
                            ) : null}
                          </div>
                        ))
                      ) : (
                        <div className="adviserPrimaryColour">
                          <span className="adviserColourLabel">
                            Primary colour
                          </span>
                          <b>-</b>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                  {header.logoUrl ? (
                    <div className="smMetaGroup">
                      <div className="smMetaLabel Content-Small muted">Download logo</div>
                      <div className="adviserLogoDownload Content-Small">
                      {["png", "svg"].map((format) => (
                        <button
                          key={format}
                          type="button"
                          disabled={Boolean(downloadingLogo)}
                          onClick={() => handleDownloadLogo(format)}
                          aria-label={`Download logo as ${format.toUpperCase()}`}
                        >
                          {downloadingLogo === format
                            ? "Downloading…"
                            : format.toUpperCase()}
                        </button>
                      ))}
                      </div>
                      {logoDownloadError ? <span className="Content-Small" role="alert">{logoDownloadError}</span> : null}
                    </div>
                  ) : null}
                </div>
                </div>

                <div className="smActionsLeft">
                  {canUploadProfile && (
                    <>
                      <PrimaryButton
                        label={
                          uploadingProfile
                            ? "Uploading..."
                            : "Upload profile image"
                        }
                        onClick={() => {
                          const input =
                            document.getElementById("profileImageInput");
                          if (input) input.click();
                        }}
                        disabled={uploadingProfile}
                      />
                      <input
                        id="profileImageInput"
                        type="file"
                        accept="image/*"
                        style={{ display: "none" }}
                        onChange={handleProfileImageUpload}
                        disabled={uploadingProfile}
                      />
                    </>

                    // <label
                    //   className="smBtnPrimary Content-Small"
                    //   style={{ cursor: "pointer" }}
                    // >
                    //   {uploadingProfile
                    //     ? "Uploading..."
                    //     : "Upload profile image"}
                    //   <input
                    //     type="file"
                    //     accept="image/*"
                    //     onChange={handleProfileImageUpload}
                    //     style={{ display: "none" }}
                    //     disabled={uploadingProfile}
                    //   />
                    // </label>
                  )}

                  {profileUploadError ? (
                    <div
                      className="Content-Small"
                      style={{ color: "red", marginTop: 6 }}
                    >
                      {profileUploadError}
                    </div>
                  ) : null}
                  {websiteError ? (
                    <div
                      className="Content-Small"
                      style={{ color: "red", marginTop: 6 }}
                    >
                      {websiteError}
                    </div>
                  ) : null}

                  {/* <div className="smChips">
                    {editingWebsite ? (
                      <div className="smChips">
                        <input
                          type="text"
                          className="Content-Small"
                          value={websiteValue}
                          onChange={(e) => setWebsiteValue(e.target.value)}
                          placeholder="Enter website URL"
                          style={{
                            minWidth: 220,
                            padding: "8px 10px",
                            borderRadius: 8,
                            border: "1px solid #ccc",
                          }}
                        />
                        <button
                          className="smBtnPrimary Content-Small"
                          onClick={handleSaveWebsite}
                          disabled={savingWebsite}
                        >
                          {savingWebsite ? "Saving..." : "Save"}
                        </button>
                        <button
                          className="smBtnGhost Content-Small"
                          onClick={() => {
                            setEditingWebsite(false);
                            setWebsiteValue(header.website || "");
                            setWebsiteError("");
                          }}
                          disabled={savingWebsite}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : header.website ? (
                      <>
                        <a
                          className="smBtnGhost Content-Small"
                          href={header.website}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Website
                        </a>
                        <button
                          className="smBtnPrimary Content-Small"
                          onClick={() => setEditingWebsite(true)}
                        >
                          Edit website
                        </button>
                      </>
                    ) : (
                      <PrimaryButton
                        label="Add website"
                        onClick={() => setEditingWebsite(true)}
                      />
                      // <button
                      //   className="smBtnPrimary Content-Small"
                      //   onClick={() => setEditingWebsite(true)}
                      // >
                      //   Add website
                      // </button>
                    )}
                  </div> */}
                </div>

                {/* {header.email && header.email !== "-" ? (
                    <a
                      className="smBtnPrimary Content-Small"
                      href={`mailto:${header.email}`}
                    >
                      Message
                    </a>
                  ) : (Entitlement
                    <button className="smBtnPrimary Content-Small" disabled>
                      Message
                    </button>
                  )} */}

                {/* {details?.personalDetails?.mobileTelephone ? (
                    <a
                      className="smBtnGhost Content-Small"
                      href={`tel:${String(
                        details.personalDetails.mobileTelephone,
                      ).replace(/\s/g, "")}`}
                    >
                      Call
                    </a>
                  ) : (
                    <button className="smBtnGhost Content-Small" disabled>
                      Call
                    </button>
                  )} */}

                {/* <button
                    className="smBtnGhost Content-Small"
                    onClick={() =>
                      navigator.clipboard?.writeText(header.email || "")
                    }
                    disabled={!header.email || header.email === "-"}
                    title="Copy email"
                  >
                    Copy email
                  </button> */}
              </div>
            </div>

            {/* RIGHT */}
            <div className="smHeaderRight">
              <div className="smLogoOnly">
                {header.profileUrl ? (
                  <img
                    className="smLogoImg"
                    src={header.profileUrl}
                    alt={header.name}
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.onerror = null;
                      e.currentTarget.src = "";
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <div className="smLogoFallback Content-SemiBold">
                    {avatarText}
                  </div>
                )}
              </div>
            </div>
          </div>

          <Tabs
            noShadow
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={(id) => {
              setActiveTab(id);
              const search = new URLSearchParams(location.search);
              search.set("tab", id);
              navigate(`${location.pathname}?${search.toString()}`, {
                replace: true,
                state,
              });
            }}
          />
          <div className="smsizedBox" />

          {/* Feed */}
          {(() => {
            switch (activeTab) {
              case "adviserDetails":
                return (
                  <AdviserDetails
                    adviserKey={adviserKey}
                    details={details}
                    loading={loading}
                    header={header}
                    canEdit={
                      userPermissions.includes("Adviser Support") ||
                      userPermissions.includes("Compliance") ||
                      userPermissions.includes("Marketing") ||
                      userPermissions.includes("Administrator")
                    }
                  />
                );

              case "clientAgreement":
                return <ClientAgreement adviserKey={adviserKey} adviserId={details?.adviserId} />;

              case "professionalQualifications":
                return <ProfessionalQualifications details={details} />;

              case "adviserCatchUp":
                return (
                  <AdviserCatchUpPanel
                    adviserId={details?.adviserId || adviserKey}
                    adviserDoc={details?.adviserDoc}
                    canEdit={false}
                    canLogCatchUp={false}
                    onEdit={() =>
                      navigate(
                        `/advisers/${encodeURIComponent(
                          details?.adviserId || adviserKey,
                        )}/catch-up/edit`,
                        {
                          state: {
                            returnTo: `/advisers/${encodeURIComponent(
                              adviserKey,
                            )}?tab=adviserCatchUp`,
                          },
                        },
                      )
                    }
                  />
                );

              case "ioDetails":
                return (
                  <IODetailsTab
                    adviserKey={adviserKey}
                    adviserEmail={header.email}
                    details={details}
                    canEdit={
                      userPermissions.includes("IO Admin") ||
                      userPermissions.includes("Administrator")
                    }
                  />
                );

              case "licenceCertificates":
                return (
                  <LicenseCertificatesTab
                    details={details}
                    loading={loading}
                    adviserEmail={header.email}
                    canEdit={
                      userPermissions.includes("Adviser Support") ||
                      userPermissions.includes("Compliance") ||
                      userPermissions.includes("Compliance Support") ||
                      userPermissions.includes("Licence Certificates")
                    }
                  />
                );

              case "rates":
                return (
                  <RatesTab
                    adviserId={details?.adviserId || adviserKey}
                    adviserEmail={header.email}
                    details={details}
                    canEdit={canAccessDepartmentRoute(
                      userPermissions,
                      APP_ROUTES.IFA_RATES,
                    )}
                  />
                );

              case "check2":
                return <AdviserAMLTab adviserEmail={header.email} />;

              case "financialPromotions":
                return <FinancialPromotionsTab adviserEmail={header.email} />;

              case "paraplanning":
                return <ParaplanningAdviserTab adviserEmail={header.email} />;

              case "proceding":
                return <ProCedingAdviserTab adviserEmail={header.email} />;

              case "charges":
                return (
                  <AdviserChargesTab
                    adviserKey={adviserKey}
                    details={details}
                    header={header}
                  />
                );

              default:
                return null;
            }
          })()}
        </div>
      </div>
    </div>
  );
}
